
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Trash2, Star, RefreshCw, ArrowUpDown } from "lucide-react";
import { Review } from "@/types/reviews";
import { useState } from "react";

interface DebugReviewsTableProps {
  isLoading: boolean;
  reviews: Review[];
  onDelete: (id: number) => void;
  onViewDetails: (review: Review) => void;
}

export const DebugReviewsTable = ({
  isLoading,
  reviews,
  onDelete,
  onViewDetails,
}: DebugReviewsTableProps) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const renderStars = (rating: number) => {
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
          }`}
        />
      ));
  };

  const sortedReviews = [...reviews].sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aValue: any = (a as any)[sortColumn] || '';
    let bValue: any = (b as any)[sortColumn] || '';
    
    let comparison = 0;
    if (sortColumn === 'rating' || sortColumn === 'id') {
      comparison = Number(aValue) - Number(bValue);
    } else if (sortColumn === 'created_at') {
      comparison = new Date(aValue).getTime() - new Date(bValue).getTime();
    } else if (sortColumn === 'highlighted') {
      const aHighlighted = aValue === true ? 1 : 0;
      const bHighlighted = bValue === true ? 1 : 0;
      comparison = aHighlighted - bHighlighted;
    } else {
      comparison = String(aValue).localeCompare(String(bValue));
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead 
              onClick={() => handleSort('id')}
              className="cursor-pointer hover:bg-gray-50 w-20"
            >
              <div className="flex items-center">
                ID <ArrowUpDown className="ml-1 h-3 w-3" />
              </div>
            </TableHead>
            <TableHead 
              onClick={() => handleSort('service_name')}
              className="cursor-pointer hover:bg-gray-50"
            >
              <div className="flex items-center">
                Service <ArrowUpDown className="ml-1 h-3 w-3" />
              </div>
            </TableHead>
            <TableHead 
              onClick={() => handleSort('customer_name')}
              className="cursor-pointer hover:bg-gray-50"
            >
              <div className="flex items-center">
                Customer <ArrowUpDown className="ml-1 h-3 w-3" />
              </div>
            </TableHead>
            <TableHead 
              onClick={() => handleSort('rating')}
              className="cursor-pointer hover:bg-gray-50 w-[120px]"
            >
              <div className="flex items-center">
                Rating <ArrowUpDown className="ml-1 h-3 w-3" />
              </div>
            </TableHead>
            <TableHead 
              onClick={() => handleSort('highlighted')}
              className="cursor-pointer hover:bg-gray-50 w-[120px]"
            >
              <div className="flex items-center">
                Highlighted <ArrowUpDown className="ml-1 h-3 w-3" />
              </div>
            </TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8">
                <div className="flex justify-center items-center space-x-2">
                  <RefreshCw className="h-5 w-5 animate-spin text-primary" />
                  <span>Loading reviews...</span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            sortedReviews.length > 0 ? (
              sortedReviews.map((review) => (
                <TableRow key={review.id} className="hover:bg-gray-50">
                  <TableCell className="font-mono text-xs">
                    {review.id}
                  </TableCell>
                  <TableCell className="font-medium">
                    {review.service_name}
                  </TableCell>
                  <TableCell>{review.customer_name}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      {renderStars(review.rating)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {review.highlighted === true ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Yes
                      </span>
                    ) : (
                      <span className="text-gray-500">No</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewDetails(review)}
                    >
                      View
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onDelete(review.id)}
                      title="Delete Review"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No reviews found.
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </div>
  );
};
